// v10/src/components/PreLandingChecklist.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, ChevronRight, Loader } from 'lucide-react';
import { speakPreLandingStep, stopSpeech } from '../utils/speechHelper';
import { preLandingService } from '../services/apiService';
import useWakeLock from '../hooks/useWakeLock';

// ── Normaliza pasos desde DB al formato interno ───────────────────
const normalizeStep = (s) => ({
  id:    s.step_id || s.id,
  icon:  s.icon        || '🛬',
  title: s.title       || '',
  desc:  s.description || s.title || '',
  voice: s.voice_text  || s.title || '',
  type:  s.step_type   || 'auto',
  delay: parseInt(s.delay_seconds) || 0,
  color: s.color       || '#0891b2',
});

// ── Pasos default (fallback offline) ─────────────────────────────
const DEFAULT_STEPS = [
  { id:'clarear_area',   icon:'👀',  title:'Clarear el área',           desc:'Verificar tráfico en el circuito antes de iniciar aproximación',      voice:'Clarear el área. Verificar tráfico en el circuito.',                   type:'manual', delay:0, color:'#dc2626' },
  { id:'rpm',            icon:'🎛️', title:'Reducir potencia',          desc:'Reducir a 2000 RPM — 1200 pies de altitud',                          voice:'Mil doscientos pies. Reducir potencia a dos mil RPM.',                 type:'manual', delay:0, color:'#2563eb' },
  { id:'caliente_bomba', icon:'🌡️', title:'Aire caliente + Bomba ON',  desc:'Aplicar aire caliente al carburador · Encender bomba de combustible', voice:'Aplicar aire caliente al carburador y encender bomba de combustible.', type:'auto',   delay:2, color:'#d97706' },
  { id:'flap1',          icon:'🛬',  title:'Flaps — primer punto',      desc:'Extender flaps al primer punto de configuración',                    voice:'Flaps, primer punto.',                                                 type:'auto',   delay:5, color:'#7c3aed' },
  { id:'gate_final',     icon:'🏁',  title:'¿Estás en la recta final?', desc:'Tocá el botón cuando ingreses a la recta final',                     voice:'Avisame cuando estés en la recta final.',                              type:'gate',   delay:0, color:'#0891b2' },
  { id:'vel_planeo',     icon:'✈️',  title:'Mantener velocidad de planeo', desc:'Mantener velocidad de planeo configurada para esta aeronave',     voice:'Mantener velocidad de planeo configurada para esta aeronave.',         type:'manual', delay:0, color:'#0d9488' },
  { id:'luces_flap2',    icon:'💡',  title:'Luces encendidas + Flaps 2',desc:'Encender luces de aterrizaje · Extender flaps al segundo punto',     voice:'Luces encendidas y flaps segundo punto. Listo para aterrizar.',       type:'auto',   delay:3, color:'#16a34a' },
];

// ── Componente principal ──────────────────────────────────────────
const PreLandingChecklist = ({ aircraft, darkMode, onClose, config = {} }) => {
  const [steps, setSteps]          = useState([]);
  const [loadingSteps, setLoading] = useState(true);
  const [isDefault, setIsDefault]  = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted]     = useState([]);
  const [phase, setPhase]             = useState('idle');
  const [countdown, setCountdown]     = useState(null);
  const timerRef = useRef(null);
  const stepRef  = useRef(0);
  const stepsRef = useRef([]);

  const isDone = phase === 'done';

  useWakeLock(phase !== 'idle');

  // ── Cargar pasos desde la DB ──────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res  = await preLandingService.getSteps(aircraft.id);
        const data = (res.data?.data || []).map(normalizeStep);
        const finalSteps = data.length > 0 ? data : DEFAULT_STEPS;
        setSteps(finalSteps);
        stepsRef.current = finalSteps;
        setIsDefault(res.data?.is_default || data.length === 0);
      } catch {
        setSteps(DEFAULT_STEPS);
        stepsRef.current = DEFAULT_STEPS;
        setIsDefault(true);
      } finally { setLoading(false); }
    };
    if (aircraft?.id) load();
  }, [aircraft?.id]);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    stopSpeech();
  }, []);

  const step = steps[currentStep];

  const speakAndAdvance = (stepObj, afterDelay) => {
    speakPreLandingStep(stepObj.voice, () => {
      if (stepObj.type === 'auto' && afterDelay > 0) {
        let secs = afterDelay;
        setCountdown(secs);
        timerRef.current = setInterval(() => {
          secs--;
          setCountdown(c => c !== null ? secs : null);
          if (secs <= 0) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setCountdown(null);
            const cur  = stepRef.current;
            const next = cur + 1;
            if (next >= stepsRef.current.length) {
              setCompleted(prev => [...prev, stepsRef.current[cur].id]);
              setPhase('done');
            } else {
              setCompleted(prev => [...prev, stepsRef.current[cur].id]);
              stepRef.current = next;
              setCurrentStep(next);
              const nextStep = stepsRef.current[next];
              setPhase('running');
              setTimeout(() => speakAndAdvance(nextStep, nextStep.delay || 0), 300);
            }
          }
        }, 1000);
      } else if (stepObj.type === 'gate') {
        setPhase('gate');
      }
    });
  };

  const handleStart = () => {
    stepRef.current  = 0;
    stepsRef.current = steps;
    setPhase('running');
    speakAndAdvance(steps[0], 0);
  };

  const handleConfirm = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    setCountdown(null);
    const cur  = stepRef.current;
    const next = cur + 1;
    if (next >= stepsRef.current.length) {
      setCompleted(prev => [...prev, stepsRef.current[cur].id]);
      setPhase('done');
      return;
    }
    setCompleted(prev => [...prev, stepsRef.current[cur].id]);
    stepRef.current = next;
    setCurrentStep(next);
    const nextStep = stepsRef.current[next];
    setPhase('running');
    speakAndAdvance(nextStep, nextStep.delay || 0);
  };

  const bgColor   = darkMode ? 'rgba(10,15,30,0.97)' : 'white';
  const textColor = darkMode ? 'white' : '#0f172a';
  const subColor  = darkMode ? 'rgba(255,255,255,0.4)' : '#64748b';

  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:100, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={onClose}>
      <motion.div
        initial={{ scale:0.9, y:24 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:24 }}
        transition={{ type:'spring', stiffness:140, damping:18 }}
        style={{
          width:'100%', maxWidth:'400px', maxHeight:'90vh',
          borderRadius:'28px', background:bgColor,
          boxShadow:'0 24px 80px rgba(0,0,0,0.4)',
          border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0',
          display:'flex', flexDirection:'column', overflow:'hidden',
        }}
        onClick={e => e.stopPropagation()}>

        {/* Header — fijo, no scrollea */}
        <div style={{ flexShrink:0, padding:'20px 20px 16px', background:'linear-gradient(135deg,rgba(8,145,178,0.15),rgba(124,58,237,0.1))', borderBottom: darkMode?'1px solid rgba(255,255,255,0.07)':'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#0891b2,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>🛬</div>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ margin:0, fontSize:16, fontWeight:800, color:textColor }}>Pre-aterrizaje</p>
              <p style={{ margin:0, fontSize:11, color:subColor, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {aircraft?.name || `${aircraft?.manufacturer} ${aircraft?.model_name}`}
                {aircraft?.registration && ` · ${aircraft.registration}`}
              </p>
            </div>
            <button onClick={onClose} style={{ flexShrink:0, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', border:'none', borderRadius:10, width:32, height:32, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:subColor }}>
              <X style={{ width:16, height:16 }} />
            </button>
          </div>
        </div>

        {/* Body — scrolleable */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px', WebkitOverflowScrolling:'touch' }}>

          {/* Cargando pasos */}
          {loadingSteps && (
            <div style={{ textAlign:'center', padding:'32px 0' }}>
              <Loader style={{ width:28, height:28, color:'#0891b2', animation:'spin 1s linear infinite', margin:'0 auto 12px' }} />
              <p style={{ margin:0, fontSize:13, color:subColor }}>
                Cargando pasos para {aircraft?.name || aircraft?.model_name}...
              </p>
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
            </div>
          )}

          {/* Estado: idle */}
          {!loadingSteps && phase === 'idle' && (
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} style={{ textAlign:'center' }}>
              <motion.div animate={{ y:[0,-6,0] }} transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }}
                style={{ fontSize:56, marginBottom:12 }}>🛬</motion.div>
              <h3 style={{ margin:'0 0 8px', fontSize:20, fontWeight:900, color:textColor }}>Listo para iniciar</h3>
              <p style={{ margin:'0 0 20px', fontSize:13, color:subColor, lineHeight:1.6 }}>
                Valentina te guiará en los {steps.length} pasos del checklist de aproximación y aterrizaje.
              </p>

              <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:20, textAlign:'left' }}>
                {steps.map((s, i) => (
                  <div key={s.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:12, background: darkMode?'rgba(255,255,255,0.04)':'#f8fafc', border: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #e2e8f0' }}>
                    <span style={{ fontSize:18, width:28, textAlign:'center' }}>{s.icon}</span>
                    <div style={{ flex:1 }}>
                      <p style={{ margin:0, fontSize:12, fontWeight:700, color:textColor }}>{s.title}</p>
                      {s.type === 'gate'               && <p style={{ margin:0, fontSize:10, color:'#0891b2' }}>Confirmación manual</p>}
                      {s.type === 'auto' && s.delay > 0 && <p style={{ margin:0, fontSize:10, color:subColor }}>Auto en {s.delay}s</p>}
                      {s.type === 'manual'              && <p style={{ margin:0, fontSize:10, color:subColor }}>Confirmación manual</p>}
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, color:s.color, background:s.color+'15', padding:'2px 8px', borderRadius:20 }}>
                      {i + 1}
                    </span>
                  </div>
                ))}
              </div>

              <motion.button onClick={handleStart}
                whileHover={{ scale:1.02, y:-2 }} whileTap={{ scale:0.97 }}
                style={{ width:'100%', padding:'16px', border:'none', borderRadius:18, cursor:'pointer', fontFamily:'inherit', fontWeight:800, fontSize:16, color:'white', background:'linear-gradient(135deg,#0891b2,#7c3aed)', boxShadow:'0 8px 24px rgba(8,145,178,0.35)', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                <span style={{ fontSize:20 }}>🛬</span> Iniciar secuencia
              </motion.button>
            </motion.div>
          )}

          {/* Estado: running o gate */}
          {!loadingSteps && (phase === 'running' || phase === 'gate') && !isDone && step && (
            <motion.div key={currentStep} initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} style={{ textAlign:'center' }}>
              <div style={{ display:'flex', gap:6, justifyContent:'center', marginBottom:20 }}>
                {steps.map((s, i) => (
                  <div key={s.id} style={{ height:4, flex:1, borderRadius:2,
                    background: completed.includes(s.id) ? s.color : i === currentStep ? s.color+'80' : darkMode?'rgba(255,255,255,0.1)':'#e2e8f0',
                    transition:'background 0.3s' }} />
                ))}
              </div>

              <motion.div animate={{ scale:[1,1.05,1] }} transition={{ duration:0.5 }}
                style={{ fontSize:60, marginBottom:12 }}>{step.icon}</motion.div>

              <p style={{ margin:'0 0 4px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:step.color }}>
                Paso {currentStep + 1} de {steps.length}
              </p>
              <h3 style={{ margin:'0 0 8px', fontSize:22, fontWeight:900, color:textColor }}>{step.title}</h3>
              <p style={{ margin:'0 0 24px', fontSize:13, color:subColor, lineHeight:1.6 }}>{step.desc}</p>

              {countdown !== null && (
                <motion.div initial={{ scale:0 }} animate={{ scale:1 }}
                  style={{ width:64, height:64, borderRadius:'50%', background:step.color+'15', border:`3px solid ${step.color}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', fontSize:24, fontWeight:900, color:step.color }}>
                  {countdown}
                </motion.div>
              )}

              {step.type === 'manual' && phase === 'running' && countdown === null && (
                <motion.button onClick={handleConfirm}
                  whileHover={{ scale:1.02, y:-2 }} whileTap={{ scale:0.97 }}
                  style={{ width:'100%', padding:'16px', border:'none', borderRadius:18, cursor:'pointer', fontFamily:'inherit', fontWeight:800, fontSize:16, color:'white', background:`linear-gradient(135deg,${step.color},${step.color}cc)`, boxShadow:`0 8px 24px ${step.color}40`, display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                  <CheckCircle style={{ width:20, height:20 }} /> Confirmado
                </motion.button>
              )}

              {step.type === 'gate' && phase === 'gate' && (
                <motion.button onClick={handleConfirm}
                  animate={{ scale:[1,1.02,1] }} transition={{ duration:1.5, repeat:Infinity }}
                  whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}
                  style={{ width:'100%', padding:'18px', border:'none', borderRadius:18, cursor:'pointer', fontFamily:'inherit', fontWeight:800, fontSize:16, color:'white', background:`linear-gradient(135deg,${step.color},${step.color}cc)`, boxShadow:`0 8px 28px ${step.color}50`, display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                  <ChevronRight style={{ width:20, height:20 }} /> Estoy en la final
                </motion.button>
              )}
            </motion.div>
          )}

          {/* Estado: done */}
          {!loadingSteps && isDone && (
            <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }} style={{ textAlign:'center', padding:'8px 0' }}>
              <motion.div animate={{ rotate:[0,10,-10,0], scale:[1,1.1,1] }} transition={{ duration:0.6 }}
                style={{ fontSize:60, marginBottom:16 }}>✅</motion.div>
              <h3 style={{ margin:'0 0 8px', fontSize:22, fontWeight:900, color:textColor }}>¡Secuencia completa!</h3>
              <p style={{ margin:'0 0 24px', fontSize:13, color:subColor, lineHeight:1.6 }}>
                Todos los pasos del checklist pre-aterrizaje fueron completados.<br/>
                <strong style={{ color:textColor }}>Buen aterrizaje 🛬</strong>
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:20, textAlign:'left' }}>
                {steps.map(s => (
                  <div key={s.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:12, background:s.color+'0D', border:`1px solid ${s.color}25` }}>
                    <CheckCircle style={{ width:16, height:16, color:s.color, flexShrink:0 }} />
                    <span style={{ fontSize:12, fontWeight:600, color:textColor }}>{s.title}</span>
                  </div>
                ))}
              </div>
              <motion.button onClick={onClose}
                whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                style={{ width:'100%', padding:'14px', border:'none', borderRadius:16, cursor:'pointer', fontFamily:'inherit', fontWeight:700, fontSize:15, color:'white', background:'linear-gradient(135deg,#16a34a,#15803d)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                Cerrar
              </motion.button>
            </motion.div>
          )}

        </div>
      </motion.div>
    </motion.div>
  );
};

export default PreLandingChecklist;
